import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { motion } from 'framer-motion';
import {
    CreditCard,
    Clock,
    Calendar,
    CalendarCheck,
    Wallet,
    ArrowRight,
    CheckCircle,
    XCircle,
    AlertCircle,
    Ban,
    RefreshCw,
    DollarSign,
    Building,
    User,
    Percent,
} from 'lucide-react';

import s from './PaymentFlowTimeline.css';
import CurrencyConverter from '../../CurrencyConverter';

const CRON_TIMEZONE = 'America/New_York';
const CRON_HOUR = 0; // 12:00 AM

class PaymentFlowTimeline extends React.Component {
    static propTypes = {
        data: PropTypes.object.isRequired,
    };

    constructor(props) {
        super(props);
        this.state = {
            countdown: this.calculateCountdown(),
        };
    }

    componentDidMount() {
        this.countdownInterval = setInterval(() => {
            this.setState({ countdown: this.calculateCountdown() });
        }, 1000);
    }

    componentWillUnmount() {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
        }
    }

    calculateCountdown() {
        const now = moment().tz(CRON_TIMEZONE);
        let nextRun = moment().tz(CRON_TIMEZONE).startOf('day').hour(CRON_HOUR);

        if (now.isAfter(nextRun)) {
            nextRun = nextRun.add(1, 'day');
        }

        const diff = nextRun.diff(now);
        const duration = moment.duration(diff);

        return {
            hours: Math.floor(duration.asHours()),
            minutes: duration.minutes(),
            seconds: duration.seconds(),
            nextRunFormatted: nextRun.format('MMM DD, hh:mm A') + ' EST',
        };
    }

    // Sanitize numeric value to avoid NaN
    safeNumber(value) {
        const num = parseFloat(value);
        return isNaN(num) ? 0 : num;
    }

    getTimelineSteps() {
        const { data } = this.props;
        const now = moment();
        const createdAt = data.createdAt ? moment(data.createdAt) : moment();
        const checkIn = data.checkIn ? moment(data.checkIn) : moment();
        const checkOut = data.checkOut ? moment(data.checkOut) : moment();
        const fundsAvailableDate = createdAt.clone().add(7, 'days');

        const steps = [];

        const total = this.safeNumber(data.total);
        const guestServiceFee = this.safeNumber(data.guestServiceFee);
        // Use processingFee from DB instead of calculating (more accurate)
        const processingFee = this.safeNumber(data.processingFee);

        // 1. Payment Received - uses calculateGuestPaid() for accurate total
        const paymentReceived = !!data.transaction;
        const guestPaidAmount = this.calculateGuestPaid();
        steps.push({
            id: 'payment',
            icon: CreditCard,
            label: 'Pago Recibido',
            status: paymentReceived ? 'completed' : 'pending',
            amount: guestPaidAmount,
            currency: data.currency || 'USD',
            date: createdAt.format('MMM DD, YYYY'),
            subtitle: paymentReceived && data.transaction?.transactionId ? `ID: ${data.transaction.transactionId.slice(0, 12)}...` : null,
        });

        // 2. Funds Hold
        const fundsReleased = now.isAfter(fundsAvailableDate);
        steps.push({
            id: 'hold',
            icon: Clock,
            label: 'Retención Stripe',
            status: fundsReleased ? 'completed' : 'active',
            subtitle: fundsReleased ? 'Liberados' : `${fundsAvailableDate.diff(now, 'days')} días restantes`,
            date: fundsAvailableDate.format('MMM DD'),
        });

        // 3. Check-in
        const checkedIn = now.isAfter(checkIn);
        steps.push({
            id: 'checkin',
            icon: Calendar,
            label: 'Check-in',
            status: checkedIn ? 'completed' : 'pending',
            date: checkIn.format('MMM DD, YYYY'),
            subtitle: data.startTime ? `${data.startTime}:00` : null,
        });

        // 4. Check-out
        const checkedOut = data.reservationState === 'completed' || now.isAfter(checkOut);
        steps.push({
            id: 'checkout',
            icon: CalendarCheck,
            label: 'Check-out',
            status: checkedOut ? 'completed' : now.isBetween(checkIn, checkOut) ? 'active' : 'pending',
            date: checkOut.format('MMM DD, YYYY'),
            subtitle: data.endTime ? `${data.endTime}:00` : null,
        });

        // 5. Funds Available
        steps.push({
            id: 'funds',
            icon: Wallet,
            label: 'Fondos Disponibles',
            status: fundsReleased ? 'completed' : 'pending',
            date: fundsAvailableDate.format('MMM DD'),
        });

        // 6. Payout to Host
        const hostPayout = this.calculateHostPayout();
        let payoutStatus = 'pending';
        let payoutSubtitle = 'En cola';

        if (data.hostTransaction) {
            payoutStatus = 'completed';
            payoutSubtitle = 'Transferido';
        } else if (data.hostFailedTransaction) {
            payoutStatus = 'failed';
            payoutSubtitle = data.hostFailedTransaction.reason?.slice(0, 50) || 'Error';
        } else if (!data.hostPayout) {
            payoutStatus = 'no-method';
            payoutSubtitle = 'Sin método de pago';
        } else if (data.isHold) {
            payoutStatus = 'hold';
            payoutSubtitle = 'Retenido por admin';
        }

        steps.push({
            id: 'payout',
            icon: payoutStatus === 'completed' ? CheckCircle : payoutStatus === 'failed' ? XCircle : ArrowRight,
            label: 'Payout al Host',
            status: payoutStatus,
            amount: hostPayout,
            currency: data.currency,
            subtitle: payoutSubtitle,
            attempts: data.paymentAttempt || 0,
        });

        return steps;
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // CÁLCULOS - Lógica copiada de Receipt.js para coherencia
    // ═══════════════════════════════════════════════════════════════════════════

    calculateDayPrice() {
        const { data } = this.props;
        const basePrice = this.safeNumber(data.basePrice);
        const dayDifference = this.safeNumber(data.dayDifference) || 1;
        const isHourly = data.isHourly;
        const startTime = this.safeNumber(data.startTime);
        const endTime = this.safeNumber(data.endTime);

        if (isHourly) {
            const hourDiff = endTime - startTime;
            const hours = hourDiff >= 0 ? hourDiff : (24 - startTime + endTime);
            return basePrice * (hours > 0 ? hours : 1);
        }
        return basePrice * dayDifference;
    }

    calculateAddonsTotal() {
        const { data } = this.props;
        if (!data.reservationAddons || data.reservationAddons.length === 0) {
            return 0;
        }
        return data.reservationAddons.reduce((sum, addon) =>
            sum + this.safeNumber(addon.totalPrice), 0);
    }

    calculateHostPayout() {
        const { data } = this.props;

        // Si hay cancelación, usar el monto de la cancelación
        if (data.cancellationDetails && data.cancellationDetails.payoutToHost !== undefined) {
            return this.safeNumber(data.cancellationDetails.payoutToHost);
        }

        const dayPrice = this.calculateDayPrice();
        const delivery = this.safeNumber(data.delivery);
        const discount = this.safeNumber(data.discount);
        const addonsTotal = this.calculateAddonsTotal();
        const featuredCommission = this.safeNumber(data.featuredCommissionAmount);

        // hostBaseEarnings = dayPrice + delivery - discount
        const hostBaseEarnings = dayPrice + delivery - discount;
        // hostEarningsWithAddons = hostBaseEarnings + addonsTotal
        const hostEarningsWithAddons = hostBaseEarnings + addonsTotal;

        // Recalcular hostServiceFee sobre (base + addons)
        let calculatedHostServiceFee = this.safeNumber(data.hostServiceFee);
        if (data.hostServiceFeeType && data.hostServiceFeeValue) {
            if (data.hostServiceFeeType === 'percentage') {
                calculatedHostServiceFee = hostEarningsWithAddons * (this.safeNumber(data.hostServiceFeeValue) / 100);
            } else {
                calculatedHostServiceFee = this.safeNumber(data.hostServiceFeeValue);
            }
        }

        // Payout = earnings - serviceFee - featuredCommission
        return hostEarningsWithAddons - calculatedHostServiceFee - featuredCommission;
    }

    calculateGuestPaid() {
        const { data } = this.props;
        // El campo 'total' de la reservación YA INCLUYE:
        // base + addons + guestServiceFee + processingFee
        // NO hay que sumar fees de nuevo (evitar doble conteo)
        return this.safeNumber(data.total);
    }

    calculateDistribution() {
        const { data } = this.props;
        const guestServiceFee = this.safeNumber(data.guestServiceFee);
        const processingFee = this.safeNumber(data.processingFee);
        const hostPayout = this.calculateHostPayout();
        const totalPaid = this.calculateGuestPaid();

        // Recalcular hostServiceFee para mostrar correctamente
        const dayPrice = this.calculateDayPrice();
        const delivery = this.safeNumber(data.delivery);
        const discount = this.safeNumber(data.discount);
        const addonsTotal = this.calculateAddonsTotal();
        const hostEarningsWithAddons = (dayPrice + delivery - discount) + addonsTotal;

        let calculatedHostServiceFee = this.safeNumber(data.hostServiceFee);
        if (data.hostServiceFeeType && data.hostServiceFeeValue) {
            if (data.hostServiceFeeType === 'percentage') {
                calculatedHostServiceFee = hostEarningsWithAddons * (this.safeNumber(data.hostServiceFeeValue) / 100);
            } else {
                calculatedHostServiceFee = this.safeNumber(data.hostServiceFeeValue);
            }
        }

        // Platform fee = guestServiceFee + hostServiceFee (recalculado)
        const platformFee = guestServiceFee + calculatedHostServiceFee;

        // Protect against division by zero
        const getPercentage = (amount) => {
            if (totalPaid <= 0) return '0.0';
            return ((amount / totalPaid) * 100).toFixed(1);
        };

        return [
            {
                label: 'Host',
                icon: User,
                amount: hostPayout,
                percentage: getPercentage(hostPayout),
                color: '#10B981',
            },
            {
                label: 'Navega',
                icon: Building,
                amount: platformFee,
                percentage: getPercentage(platformFee),
                color: '#3B82F6',
            },
            {
                label: 'Stripe',
                icon: CreditCard,
                amount: processingFee,
                percentage: getPercentage(processingFee),
                color: '#8B5CF6',
            },
        ];
    }

    renderStep(step, index, total) {
        const isLast = index === total - 1;
        const statusColors = {
            completed: '#10B981',
            active: '#F59E0B',
            pending: '#6B7280',
            failed: '#EF4444',
            hold: '#3B82F6',
            'no-method': '#9CA3AF',
        };

        const Icon = step.icon;
        const color = statusColors[step.status] || '#6B7280';

        return (
            <div key={step.id} className={s.stepContainer}>
                <motion.div
                    className={cx(s.step, s[`step-${step.status}`])}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ delay: index * 0.1 }}
                >
                    <div className={s.iconWrapper} style={{ backgroundColor: color }}>
                        <Icon size={20} color="#fff" />
                    </div>
                    <div className={s.stepContent}>
                        <div className={s.stepLabel}>{step.label}</div>
                        {step.amount !== undefined && (
                            <div className={s.stepAmount}>
                                <CurrencyConverter amount={step.amount} from={step.currency} />
                            </div>
                        )}
                        {step.date && <div className={s.stepDate}>{step.date}</div>}
                        {step.subtitle && <div className={s.stepSubtitle}>{step.subtitle}</div>}
                        {step.attempts > 0 && (
                            <div className={s.attempts}>
                                <RefreshCw size={12} /> {step.attempts}/3 intentos
                            </div>
                        )}
                    </div>
                </motion.div>
                {!isLast && (
                    <div className={cx(s.connector, step.status === 'completed' && s.connectorCompleted)} />
                )}
            </div>
        );
    }

    renderCancellation() {
        const { data } = this.props;
        if (data.reservationState !== 'cancelled' || !data.cancellationDetails) {
            return null;
        }

        const { cancellationDetails } = data;
        const cancelledBy = cancellationDetails.cancelledBy === 'guest' ? 'Huésped' : 'Host';

        return (
            <motion.div
                className={s.cancellationBranch}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
            >
                <div className={s.cancellationHeader}>
                    <Ban size={18} color="#EF4444" />
                    <span>Cancelación</span>
                </div>
                <div className={s.cancellationDetails}>
                    <div className={s.cancellationItem}>
                        <span className={s.cancellationLabel}>Cancelado por:</span>
                        <span className={s.cancellationValue}>{cancelledBy}</span>
                    </div>
                    <div className={s.cancellationItem}>
                        <span className={s.cancellationLabel}>Política:</span>
                        <span className={s.cancellationValue}>{cancellationDetails.cancellationPolicy}</span>
                    </div>
                    <div className={s.cancellationItem}>
                        <span className={s.cancellationLabel}>Refund al Guest:</span>
                        <span className={s.cancellationValue}>
                            <CurrencyConverter amount={cancellationDetails.refundToGuest} from={cancellationDetails.currency} />
                        </span>
                    </div>
                    <div className={s.cancellationItem}>
                        <span className={s.cancellationLabel}>Payout al Host:</span>
                        <span className={s.cancellationValue}>
                            <CurrencyConverter amount={cancellationDetails.payoutToHost} from={cancellationDetails.currency} />
                        </span>
                    </div>
                </div>
            </motion.div>
        );
    }

    renderDistribution() {
        const distribution = this.calculateDistribution();
        const { data } = this.props;
        const totalPaid = this.calculateGuestPaid();

        return (
            <motion.div
                className={s.distributionSection}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 }}
            >
                <div className={s.distributionHeader}>
                    <DollarSign size={18} />
                    <span>Distribución de <CurrencyConverter amount={totalPaid} from={data.currency} /></span>
                </div>
                <div className={s.distributionBars}>
                    {distribution.map((item, index) => {
                        const Icon = item.icon;
                        return (
                            <div key={index} className={s.distributionItem}>
                                <div className={s.distributionInfo}>
                                    <Icon size={14} style={{ color: item.color }} />
                                    <span className={s.distributionLabel}>{item.label}</span>
                                    <span className={s.distributionAmount}>
                                        <CurrencyConverter amount={item.amount} from={this.props.data.currency} />
                                    </span>
                                    <span className={s.distributionPercent}>({item.percentage}%)</span>
                                </div>
                                <div className={s.distributionBar}>
                                    <motion.div
                                        className={s.distributionFill}
                                        style={{ backgroundColor: item.color }}
                                        initial={{ width: 0 }}
                                        animate={{ width: `${item.percentage}%` }}
                                        transition={{ delay: 0.8 + index * 0.1, duration: 0.5 }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </motion.div>
        );
    }

    renderPayoutHistory() {
        const { data } = this.props;
        const history = data.failedTransactionHistory || [];
        const hasSuccessfulPayout = !!data.hostTransaction;

        // Si no hay historial y no hay pago exitoso, no mostrar nada
        if (history.length === 0 && !hasSuccessfulPayout) {
            return null;
        }

        return (
            <motion.div
                className={s.historySection}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
            >
                <div className={s.historyHeader}>
                    <RefreshCw size={18} />
                    <span>Historial de Intentos de Pago</span>
                </div>
                <div className={s.historyList}>
                    {/* Pago exitoso primero si existe */}
                    {hasSuccessfulPayout && (
                        <div className={cx(s.historyItem, s.historySuccess)}>
                            <div className={s.historyStatus}>
                                <CheckCircle size={16} color="#10B981" />
                            </div>
                            <div className={s.historyContent}>
                                <div className={s.historyDate}>
                                    {moment(data.hostTransaction.createdAt).format('DD/MM/YYYY HH:mm')}
                                </div>
                                <div className={s.historyMessage}>
                                    Pago completado exitosamente
                                </div>
                                {data.hostTransaction.transactionId && (
                                    <div className={s.historyCode}>
                                        ID: {data.hostTransaction.transactionId}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Intentos fallidos */}
                    {history.map((attempt, index) => (
                        <div key={attempt.id} className={cx(s.historyItem, s.historyFailed)}>
                            <div className={s.historyStatus}>
                                <XCircle size={16} color="#EF4444" />
                            </div>
                            <div className={s.historyContent}>
                                <div className={s.historyDate}>
                                    {moment(attempt.createdAt).format('DD/MM/YYYY HH:mm')}
                                </div>
                                <div className={s.historyMessage}>
                                    {attempt.reason || 'Error desconocido'}
                                </div>
                                <div className={s.historyAmount}>
                                    <CurrencyConverter amount={attempt.amount} from={data.currency || 'USD'} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </motion.div>
        );
    }

    renderCronCountdown() {
        const { data } = this.props;
        const { countdown } = this.state;

        // Only show if payout is pending
        if (data.hostTransaction || data.reservationState === 'cancelled') {
            return null;
        }

        const needsPayout = data.reservationState === 'completed' && !data.hostTransaction && !data.isHold;

        if (!needsPayout) {
            return null;
        }

        return (
            <motion.div
                className={s.cronSection}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.9 }}
            >
                <div className={s.cronHeader}>
                    <Clock size={18} />
                    <span>Próximo Auto-Payout</span>
                </div>
                <div className={s.cronCountdown}>
                    <div className={s.cronTime}>
                        <span className={s.cronNumber}>{String(countdown.hours).padStart(2, '0')}</span>
                        <span className={s.cronLabel}>hrs</span>
                    </div>
                    <span className={s.cronSeparator}>:</span>
                    <div className={s.cronTime}>
                        <span className={s.cronNumber}>{String(countdown.minutes).padStart(2, '0')}</span>
                        <span className={s.cronLabel}>min</span>
                    </div>
                    <span className={s.cronSeparator}>:</span>
                    <div className={s.cronTime}>
                        <span className={s.cronNumber}>{String(countdown.seconds).padStart(2, '0')}</span>
                        <span className={s.cronLabel}>seg</span>
                    </div>
                </div>
                <div className={s.cronNext}>
                    Ejecuta: {countdown.nextRunFormatted}
                </div>
                {data.paymentAttempt > 0 && (
                    <div className={s.cronAttempts}>
                        <AlertCircle size={14} />
                        Intentos: {data.paymentAttempt}/3
                    </div>
                )}
            </motion.div>
        );
    }

    render() {
        const { data } = this.props;
        const steps = this.getTimelineSteps();

        // Determinar estado real del PAYOUT (no de la reservación)
        let payoutStatus = 'pending';
        let payoutLabel = 'Pending';

        if (data.hostTransaction) {
            payoutStatus = 'paid';
            payoutLabel = 'Paid';
        } else if (!data.hostPayout || !data.hostPayout.payEmail) {
            payoutStatus = 'no-method';
            payoutLabel = 'Sin Método';
        } else if (data.hostFailedTransaction) {
            payoutStatus = 'failed';
            payoutLabel = 'Failed';
        }

        return (
            <div className={s.container}>
                <div className={s.header}>
                    <DollarSign size={24} />
                    <h3>Flujo de Pago - Reserva #{data.id}</h3>
                    <span className={cx(s.badge, s[`badge-${payoutStatus}`])}>
                        {payoutLabel}
                    </span>
                </div>

                <div className={s.timeline}>
                    {steps.map((step, index) => this.renderStep(step, index, steps.length))}
                </div>

                {this.renderCancellation()}
                {this.renderDistribution()}
                {this.renderPayoutHistory()}
                {this.renderCronCountdown()}
            </div>
        );
    }
}

export default withStyles(s)(PaymentFlowTimeline);
